import React, { useEffect, useState } from 'react'
import { DatePicker, Form, InputNumber, Radio, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { DashboardSettingType, QueryExpression } from '../../../constants/DashboardConf'
import Filter from './Filter'
import { DataType } from '../../../constants/DataType'
import { fetchIndicators } from '../../../apis/dashboard'

const { AVG, SUM } = QueryExpression
const { INT_COMPLETE, STR_IN, STR_EQUAL_ONLY } = DataType
const { PIECHART, SUMMARY } = DashboardSettingType
const conf = {
  default: { useXAxis: true, dimensionUseBasic: true, yAxisMultiple: true, disabledIntervalDay: false, hideFilter: false, filterOptPresistType: false, dimChangeExtraFunc: () => {} },
  [PIECHART]: { useXAxis: false, dimensionUseBasic: false, yAxisMultiple: false },
  [SUMMARY]: {
    useXAxis: false,
    yAxisMultiple: false,
    disabledIntervalDay: true,
    hideFilter: true,
    filterOptPresistType: STR_EQUAL_ONLY,
    dimChangeExtraFunc: ({ dimension, setCounter }) => setCounter(dimension ? [1] : [])
  }
}

function IndicatorSetting (props) {
  const { defPlaceholder, defConfig, indicators, setIndicators, form, counter, setCounter, editData, dimensions, data, type, OrderAndLimit } = props
  const { t: ln_t } = useTranslation()
  const { useXAxis, dimensionUseBasic, yAxisMultiple, disabledIntervalDay, hideFilter, filterOptPresistType, dimChangeExtraFunc } = { ...conf.default, ...conf[type] || {} }

  const [initSetup, setInitSetup] = useState(false)
  const [filterOpt, setFilterOpt] = useState([])
  const [filterMapping, setFilterMapping] = useState([])
  const formVal = Form.useWatch() || {}
  const isMonthly = formVal.indicatorDateGranularity === 'monthly'
  const useDateFilter = formVal.dateFilter

  const getIndicatorOpt = async ({ dimensionId = formVal.dimensionId, indicatorType }) => {
    if (dimensionId === undefined) return
    const isMonthly = (indicatorType || formVal.indicatorDateGranularity || editData?.UISetting?.indicatorDateGranularity) === 'monthly' ? 'yes' : 'no'
    const res = await fetchIndicators(data?.brandId, dimensionId, { maxResults: 999, isMonthly })
    if (res?.status === 200) setIndicators(res?.data)
  }

  const getFilterFields = () => counter?.map(i => [`${i}-column`, `${i}-operator`, `${i}-value`]).flat()
  const handleChangeXAxis = (e, reset = true, dimensionId = formVal.dimensionId) => {
    const isDimension = e?.target?.value === 'dimension'
    setCounter(isDimension || dimensionId === 0 ? [] : [1])
    const resetFields = ['period', 'timeRange', 'customRange', ...getFilterFields()]?.reduce((all, i) => ({ ...all, [i]: undefined }), {})
    if (reset) form.setFieldsValue({ ...resetFields, aggregate: isDimension ? SUM : undefined })
  }
  const handleChangeDimension = (e, reset = true) => {
    getIndicatorOpt({ dimensionId: e })
    if (e === 0) {
      form.setFieldValue('xAxis', 'date')
      handleChangeXAxis('date', reset, e)
    }
    if (reset) form.setFieldsValue(['period', 'timeRange', 'customRange', 'indicatorId', 'aggregate', ...getFilterFields()]?.reduce((all, i) => ({ ...all, [i]: undefined }), {}))
    const selectedName = dimensions?.find(i => i.id === e)?.name
    const opt = e === 0
      ? { value: 'value', label: 'value', type: INT_COMPLETE }
      : { value: 'group_type1', label: selectedName, type: filterOptPresistType || STR_IN }
    setFilterOpt([opt])
    setFilterMapping({ [opt.value]: opt })
    dimChangeExtraFunc({ dimension: e, setCounter })
  }
  const handleChangeIndicatorType = e => {
    getIndicatorOpt({ indicatorType: e?.target?.value })
    form.setFieldsValue(['period', 'timeRange', 'customRange', 'indicatorId', 'aggregate', ...getFilterFields()]?.reduce((all, i) => ({ ...all, [i]: undefined }), {}))
  }
  const handleChangeTimeRange = () => {
    form.setFieldsValue({ period: undefined, customRange: undefined, ...formVal.xAxis === 'dimension' ? {} : { aggregate: undefined } })
  }

  const periodOpt = {
    date: {
      false: [{ value: 7, label: ln_t('dashboard.last7Days') }, { value: 14, label: ln_t('dashboard.last14Days') }, { value: 30, label: ln_t('dashboard.last30Days') }],
      true: [{ value: 3, label: ln_t('dashboard.last3Months') }, { value: 6, label: ln_t('dashboard.last6Months') }, { value: 12, label: ln_t('dashboard.last1Year') }]
    },
    dimension: {
      false: [{ value: 1, label: ln_t('dashboard.last1Days') }, { value: 7, label: ln_t('dashboard.last7Days') }, { value: 14, label: ln_t('dashboard.last14Days') }, { value: 30, label: ln_t('dashboard.last30Days') }],
      true: [{ value: 1, label: ln_t('dashboard.last1Months') }, { value: 3, label: ln_t('dashboard.last3Months') }, { value: 6, label: ln_t('dashboard.last6Months') }, { value: 12, label: ln_t('dashboard.last1Year') }]
    }
  }

  useEffect(() => {
    if (editData) handleChangeDimension(editData.UISetting.dimensionId, false)
    else setInitSetup(true)
  }, [editData])

  useEffect(() => {
    if (useDateFilter === undefined) return
    if (useDateFilter && initSetup) {
      handleChangeXAxis({ target: { value: 'date' } })
    } else if (!initSetup) setInitSetup(true)
  }, [useDateFilter])

  return <>
    <Form.Item {...defConfig('dimension', 'select')} name="dimensionId" style={{ width: '100%' }}>
      <Select
        showSearch
        style={{ width: '100%' }}
        placeholder={defPlaceholder('dimension', 'select')}
        options={[...dimensionUseBasic ? [{ value: 0, label: ln_t('dashboard.basicDimension') }] : [], ...dimensions?.filter(i => i.id)?.map(i => ({ value: i.id, label: i.name }))]}
        optionFilterProp="children"
        onChange={handleChangeDimension}
      />
    </Form.Item>
    <div style={{ display: 'flex', gap: '8px' }}>
      {useXAxis && <Form.Item {...defConfig('xAxis', 'select')} initialValue="dimension" style={{ width: '100%' }}>
        <Radio.Group
          buttonStyle="solid"
          optionType="button"
          options={[
            { value: 'dimension', label: ln_t('dashboard.dimension'), disabled: formVal.dimensionId === 0 || useDateFilter },
            { value: 'date', label: ln_t('dashboard.time') }
          ]}
          onChange={handleChangeXAxis}
        />
      </Form.Item>}
      <Form.Item {...defConfig('indicatorDateGranularity', 'select')} initialValue="daily" style={{ width: '100%' }}>
        <Radio.Group
          buttonStyle="solid"
          optionType="button"
          options={[{ value: 'daily', label: ln_t('dashboard.daily') }, { value: 'monthly', label: ln_t('dashboard.monthly') }]}
          onChange={handleChangeIndicatorType}
        />
      </Form.Item>
    </div>
    <div style={{ display: 'flex', gap: '8px' }}>
      <Form.Item {...defConfig(useXAxis ? 'yAxisIndicator' : 'indicator', 'select')} name="indicatorId" style={{ width: '100%' }}>
        <Select
          showSearch
          mode={yAxisMultiple ? 'tags' : ''}
          placeholder={defPlaceholder(useXAxis ? 'yAxisIndicator' : 'indicator', 'select')}
          options={indicators?.map(i => ({ value: i.id, label: i.name }))}
          optionFilterProp="children"
          disabled={formVal.dimensionId === undefined}
        />
      </Form.Item>
      <Form.Item
        {...isMonthly ? defConfig('intervalMonths', 'input', formVal.timeRange === 'custom' && !disabledIntervalDay && formVal.xAxis !== 'dimension') : defConfig('intervalDays', 'input', formVal.timeRange === 'custom' && !disabledIntervalDay && formVal.xAxis !== 'dimension')}
        name="period"
        style={{ width: '100%' }}
      >
        <InputNumber
          placeholder={isMonthly ? defPlaceholder('intervalMonths', 'input') : defPlaceholder('intervalDays', 'input')}
          style={{ width: '100%' }}
          precision={0}
          min={0}
          disabled={!(formVal.timeRange === 'custom') || formVal.xAxis === 'dimension' || disabledIntervalDay}
        />
      </Form.Item>
    </div>
    <div style={{ display: 'flex', gap: '8px' }}>
      <Form.Item {...defConfig('timeRange', 'select')} style={{ width: '100%' }}>
        <Select
          showSearch
          placeholder={defPlaceholder('timeRange', 'select')}
          options={[...periodOpt[formVal.xAxis || 'dimension']?.[isMonthly] || [], { value: 'custom', label: ln_t('dashboard.custom') }]}
          optionFilterProp="children"
          disabled={!formVal.indicatorId}
          onChange={handleChangeTimeRange}
        />
      </Form.Item>
      <Form.Item {...defConfig('aggregate', 'select', formVal.timeRange === 'custom')} style={{ width: '100%' }} initialValue={SUM}>
        <Select
          showSearch
          placeholder={defPlaceholder('aggregate', 'select')}
          options={[{ value: SUM, label: ln_t('dashboard.sum') }, { value: AVG, label: ln_t('dashboard.avg') }]}
          optionFilterProp="children"
          disabled={!(formVal.timeRange === 'custom') && (formVal.xAxis !== 'dimension' && useXAxis)}
        />
      </Form.Item>
    </div>
    {(!useXAxis || formVal.xAxis === 'dimension') && type !== SUMMARY
      ? <div style={{ display: 'flex', gap: '8px' }}>{OrderAndLimit}</div>
      : ''}
    {formVal.timeRange === 'custom' && <Form.Item {...defConfig('customRange', 'select')} style={{ width: '100%' }}>
        <DatePicker.RangePicker picker={isMonthly ? 'month' : 'date'} style={{ width: '100%' }} />
      </Form.Item>}
    <Filter
      {...props}
      filterOpt={filterOpt}
      columnMapping={filterMapping}
      hideFilterLogic
      maxFilter={1}
      disableActions={(formVal.xAxis === 'date' && formVal.dimensionId !== 0) || hideFilter}/>
  </>
}

export default IndicatorSetting
